import React from "react";
import { Award, ArrowRight } from "lucide-react";
import { FORENSIC_AREAS } from "../data/mock";

export const Forensic = () => {
  return (
    <section
      id="forensic"
      data-testid="forensic-section"
      className="py-24 lg:py-32 bg-navy text-ivory relative overflow-hidden"
    >
      {/* Decorative gold lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #C0974E 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        {/* Left column - Heading and certification */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <div className="gold-line" />
            <span className="text-xs tracking-[0.3em] uppercase text-gold font-medium">
              Forensic Accounting Expert
            </span>
          </div>

          <h2
            data-testid="forensic-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.05] tracking-tight"
          >
            One of very few
            <br />
            <span className="italic text-gold">attorneys</span> with
            this credential.
          </h2>

          <div
            className="mt-10 inline-flex items-start gap-4 p-6 border border-gold/30 bg-gold/5 rounded-sm"
            data-testid="cff-credential-card"
          >
            <Award
              className="w-8 h-8 text-gold flex-shrink-0 mt-1"
              strokeWidth={1.25}
            />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-gold mb-1">
                AICPA Certification
              </div>
              <div className="font-serif text-xl text-ivory leading-tight">
                Certified in Financial Forensics (CFF)
              </div>
              <p className="text-sm text-ivory/70 mt-2 leading-relaxed">
                Authorized by the AICPA in May 2008. The credential
                encompasses fundamental and specialized forensic accounting
                skills CPAs apply across financial litigation.
              </p>
            </div>
          </div>
        </div>

        {/* Right column - Content */}
        <div className="lg:col-span-7 lg:pl-12 lg:border-l lg:border-gold/20">
          <p
            data-testid="forensic-intro"
            className="text-lg lg:text-xl text-ivory/85 leading-relaxed mb-10"
          >
            Dick's CFF credential reflects the depth of his work in
            financially oriented legal cases over five decades. As a
            forensic accountant and CPA, he is increasingly called upon to
            serve as a{" "}
            <strong className="text-gold">consulting expert</strong> and
            <strong className="text-gold">{" "}testifying expert witness</strong>{" "}
            in accounting and financial matters.
          </p>

          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-5">
            Forensic Specializations
          </div>

          <ul className="grid sm:grid-cols-2 gap-px bg-ivory/10 border border-ivory/10 rounded-sm overflow-hidden">
            {FORENSIC_AREAS.map((area, idx) => (
              <li
                key={area}
                data-testid={`forensic-area-${idx}`}
                className="bg-navy hover:bg-navy-deep transition-colors p-6 flex items-center justify-between group"
              >
                <span className="font-serif text-lg text-ivory">{area}</span>
                <ArrowRight
                  className="w-4 h-4 text-gold opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                  strokeWidth={1.5}
                />
              </li>
            ))}
          </ul>

          <p className="mt-8 text-sm text-ivory/65 leading-relaxed italic">
            His experience as a trial lawyer uniquely equips him to be a
            valuable member of other lawyers' trial teams — and to deliver
            persuasive, understandable financial testimony in the courtroom.
          </p>
        </div>
      </div>
    </section>
  );
};
